import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { ContentKeywordSelecting, ContentKeywordSelectingGq } from '../../../../../../graphql/projects/models/ContentKeywordSelectingGq';


export interface ContentTaskTableState {
  selectionState: ContentKeywordSelecting;
  setSelectionState: Dispatch<SetStateAction<ContentKeywordSelecting>>;
  handleSelectToggle: (id: number) => void;
  handleSelectMany: (ids: number[], selecting: boolean) => void;
  handleResetSelection: () => void;
  backendSelectionState: ContentKeywordSelectingGq;
}

export function useContentTaskTableState(
  initialSelectionState?: ContentKeywordSelecting
): ContentTaskTableState {
  const [selectionState, setSelectionState] = useState<ContentKeywordSelecting>(
    initialSelectionState ?? {
      invertSelection: false,
      contentKeywordIds: {},
      emptyIsAll: true,
    }
  );

  const handleSelectToggle = useCallback(
    (id: number) =>
      setSelectionState((prevSelectionState) => {
        let contentKeywordIds = { ...prevSelectionState.contentKeywordIds };

        const keywordIsSelected = !!contentKeywordIds[id];
        if (!keywordIsSelected) {
          contentKeywordIds[id] = true;
        } else if (!keywordIsSelected) {
          delete contentKeywordIds[id];
        }

        return { ...prevSelectionState, contentKeywordIds: contentKeywordIds };
      }),
    []
  );

  const handleSelectMany = useCallback(
    (ids: number[], selecting: boolean) =>
      setSelectionState((prevSelectionState) => {
        let keywordIds = { ...prevSelectionState.contentKeywordIds };
        let dataChanged = false;
        ids.forEach((id) => {
          const keywordIsSelected = !!keywordIds[id];
          if (selecting && !keywordIsSelected) {
            // Add selection
            keywordIds[id] = true;
            dataChanged = true;
          } else if (!selecting && keywordIsSelected) {
            // Remove selection
            delete keywordIds[id];
            dataChanged = true;
          }
        });

        if (!dataChanged) {
          return prevSelectionState;
        }

        return { ...prevSelectionState, contentKeywordIds: keywordIds };
      }),
    []
  );

  const handleResetSelection = useCallback(
    () =>
      setSelectionState(
        initialSelectionState ?? {
          invertSelection: false,
          contentKeywordIds: [],
          emptyIsAll: true,
        }
      ),
    []
  );

  const backendSelectionState = useMemo(
    (): ContentKeywordSelectingGq => ({
      invertSelection: selectionState.invertSelection,
      emptyIsAll: selectionState.emptyIsAll,
      contentKeywordIds: Object.keys(selectionState.contentKeywordIds).map((k) => +k),
    }),
    [selectionState]
  );

  return useMemo(
    () => ({
      selectionState,
      setSelectionState,
      handleResetSelection,
      handleSelectToggle,
      handleSelectMany,
      backendSelectionState,
    }),
    [selectionState]
  );
}
